import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import { EventAvailable, Repeat } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import SlotPicker from "../../components/SlotPicker/SlotPicker";
import SkeletonCard from "../../components/Loaders/SkeletonCard";
import { fetchAvailabilitiesByScheduleId, resheduleBooking } from "../../redux/actions/apiActions";
import { showAlert } from "../../redux/actions/commonActions";
import { setShowLoginForm } from "../../redux/actions/authActions";
import moment from "moment-timezone";

const MultiBookingPartial = () => {
    const userData = useSelector(state => state.auth.userData);
    const bookingDetails = useSelector(state => state.api.bookingDetails);
    const availabilities = useSelector(state => state?.api?.availabilities[bookingDetails?.tempServiceId]);
    const availabilitiesLoading = useSelector(state => state?.api?.availabilitiesLoading[bookingDetails?.id]);
    const dispatch = useDispatch();

    const [showPartialBookingModal, setShowPartialBookingModal] = useState(false);
    const [filteredAvailabilities, setFilteredAvailabilities] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [selectedSlots, setSelectedSlots] = useState(null);

    const onSelectSlot = (slot) => {
        if (bookingDetails?.multiBooking?.maxSlots) {
            setSelectedSlots(prev => {
                const safePrev = prev ?? [];
                if (safePrev.map(item => item.id).includes(slot.id)) {
                    return safePrev.filter(item => item.id != slot.id)
                }
                return safePrev.length < (bookingDetails?.multiBooking?.maxSlots ?? Infinity)
                    ? [...safePrev, slot].sort((a, b) => a.startSec - b.startSec)
                    : safePrev;
            });
        } else {
            setSelectedSlots(prev => prev?.startSec !== slot?.startSec ? slot : null)
        }
    }

    const closePartialBookingModal = () => {
        setShowPartialBookingModal(false);
        setSelectedSlots(null);
    }
    const onConfirmPartialBooking = async () => {
        if (selectedSlots?.length && bookingDetails?.id) {
            const slotIds = selectedSlots.map(item => item.id);
            let payload = {
                eventId: bookingDetails?.id,
                newAvailabilityIds: slotIds
            }
            const status = await dispatch(resheduleBooking(payload));
            if (status) {
                setShowPartialBookingModal(false);
                setSelectedSlots(null);
            }
        }
    }

    const handleButton = () => {
        const isAuthorisedUser = userData?.id &&
            (
                userData?.id === bookingDetails?.customerId ||
                userData?.email === bookingDetails?.customerEmail ||
                userData?.telephone === bookingDetails?.customerPhoneNumber
            );

        if (isAuthorisedUser) {
            setShowPartialBookingModal(true)
        } else {
            if (userData?.id) {
                dispatch(showAlert("error", "Only the account that made the original booking can create sub-bookings. Please log in with the email or phone number used for the parent booking."))
            } else {
                dispatch(setShowLoginForm(true));
            }
        }
    }

    useEffect(() => {
        if (showPartialBookingModal) {
            setIsRefreshing(true);
            dispatch(fetchAvailabilitiesByScheduleId(bookingDetails?.tempServiceId))
            //setTimeout(() => setIsRefreshing(false), 1000)
        }
    }, [showPartialBookingModal])

    useEffect(() => {

        const parentSlotSec = bookingDetails?.parentBooking?.startSec ?? bookingDetails?.startSec;
        const horizonDays = bookingDetails?.multiBooking?.horizonDays ?? 2;
        const gapMinutes = bookingDetails?.multiBooking?.gapMinutes ?? 0;
        const maxSlots = bookingDetails?.multiBooking?.maxSlots ?? Infinity;
        const bookedSlots = [
            { startSec: parentSlotSec },
            ...(bookingDetails?.subBookings ? (bookingDetails?.subBookings.map(item => ({ startSec: item.startSec }))) : [{ startSec: bookingDetails.startSec }])
        ];
        const gapInSec = gapMinutes * 60;
        const horizonEndSec = moment(parentSlotSec * 1000)
            .add(horizonDays, "days")
            .endOf("day")
            .unix();

        if (Array.isArray(selectedSlots) && selectedSlots.length) {
            const selectedSlotIds = selectedSlots.map(item => item.id);

            let filteredAvailabilities = availabilities
                .filter(item => item.startSec > parentSlotSec && item.startSec < horizonEndSec)
                .map(item => {
                    const isSelected = selectedSlotIds.includes(item.id);
                    let disabled = false;

                    if (selectedSlots.length >= maxSlots) {
                        disabled = !isSelected;
                    } else {
                        disabled = (
                            bookedSlots.some(sel => {
                                const selStart = sel.startSec;
                                return (
                                    item.startSec > selStart - gapInSec &&
                                    item.startSec < selStart + gapInSec
                                );
                            })
                        ) || (
                                selectedSlots.some(sel => {
                                    const selStart = sel.startSec;
                                    return (
                                        !isSelected &&
                                        item.startSec > selStart - gapInSec &&
                                        item.startSec < selStart + gapInSec
                                    );
                                })
                            );
                    }
                    return { ...item, disabled };
                });

            setFilteredAvailabilities(filteredAvailabilities);
        } else {
            if (availabilities) {
                let filteredAvailabilities_ = availabilities.filter(a => a.startSec > parentSlotSec && a.startSec < horizonEndSec)
                    .map(item => {
                        let disabled = bookedSlots.some(sel => {
                            const selStart = sel.startSec;
                            return (
                                item.startSec > selStart - gapInSec &&
                                item.startSec < selStart + gapInSec
                            );
                        });
                        return { ...item, disabled };
                    });
                setFilteredAvailabilities(filteredAvailabilities_);
            }
        }
    }, [availabilities, selectedSlots, bookingDetails])

    useEffect(()=>{
        if (availabilities) {
            setIsRefreshing(true);
            setTimeout(() => setIsRefreshing(false), 500)
        }
    },[availabilities, bookingDetails])

    const getSlotSelectionMessage = (minSlots = 1, maxSlots, selectedCount) => {
        const remaining = Math.max(maxSlots - selectedCount, 0);
        if (!selectedCount) {
            return `You can select ${maxSlots} slot${maxSlots !== 1 ? 's' : ''}.`
        }
        if (maxSlots === 1) {
            if (selectedCount === 0) {
                return `Please select the slot.`;
            }
            return `You’ve selected the slot.`;
        }
        if (minSlots > 1 && selectedCount < minSlots) {
            if (minSlots === maxSlots) {
                return `Please select all ${maxSlots} slots now.`;
            }
            return `Please select at least ${minSlots} slots now. ${maxSlots - minSlots} slot${(maxSlots - minSlots) !== 1 ? 's' : ''} can be booked later.`;
        }
        if (selectedCount === maxSlots) {
            return `You’ve selected all ${maxSlots} slots.`;
        }
        if (selectedCount < maxSlots) {
            return `You’ve selected ${selectedCount} of ${maxSlots} remaining slot${maxSlots !== 1 ? 's' : ''}. ${remaining} slots can be booked later.`;
        }
        return "";
    };

    if (!bookingDetails?.id) {
        return <></>
    }
    return (
        <>
            {bookingDetails?.multiBooking?.allowPartialBooking && bookingDetails?.multiBooking?.maxSlots && bookingDetails?.status !== "CANCELED"  ?
                <Button variant="outlined" sx={{ borderRadius: 5, gap: 1 }} onClick={handleButton}>
                    <EventAvailable />  Book Remaining Slots
                </Button>
                : null
            }
            <Dialog
                open={showPartialBookingModal}
                onClose={closePartialBookingModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ sx: { borderRadius: 3, minHeight: '60%', width: '100%', mx: 1 } }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title" sx={{ textAlign: 'center' }}>
                    <Typography variant="overline" >Select Time Slots</Typography>
                </DialogTitle>
                <DialogContent>
                    {availabilitiesLoading || isRefreshing ?
                        <SkeletonCard cardType="availabilities" />
                        :
                        <SlotPicker
                            availabilities={filteredAvailabilities}
                            onSelectSlot={onSelectSlot}
                            selectedSlotIds={Array.isArray(selectedSlots) ? selectedSlots.map(item => item.id) : selectedSlots?.id}
                            timezoneId={bookingDetails?.timezoneId}
                        />
                    }
                </DialogContent>
                <Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {selectedSlots?.length || bookingDetails?.multiBooking?.maxSlots > 0 ?
                        <Alert
                            sx={{
                                width: '100%', justifyContent: 'center',
                                alignItems: 'center', borderRadius: 3,
                                fontSize: "0.8rem", padding: "2px 8px",
                                "& .MuiAlert-icon": { fontSize: "1rem" },
                                "& .MuiAlert-message": { padding: 0 }
                            }}
                            severity={selectedSlots?.length < bookingDetails?.multiBooking?.minSlots ? "warning" : 'info'}
                        >
                            {getSlotSelectionMessage(bookingDetails?.multiBooking?.minSlots, bookingDetails?.multiBooking?.maxSlots, selectedSlots?.length ?? 0)}
                        </Alert>
                        : null
                    }
                    {selectedSlots?.length ?
                        selectedSlots.map(slot =>
                            <Chip
                                color={"slotPicker"}
                                variant="filled"
                                sx={{ height: 'auto', padding: "3px" }}
                                label={
                                    <>
                                        {moment(slot?.startSec * 1000).tz(bookingDetails?.timezoneId).format("h:mm A, ")}
                                        {moment(slot?.startSec * 1000).tz(bookingDetails?.timezoneId).format("Do MMM")}
                                    </>
                                }
                                onDelete={() => setSelectedSlots(prev => prev.filter(itm => itm.id !== slot.id))}
                            />
                        )
                        : null
                    }
                </Box>
                <DialogActions>
                    <Button onClick={closePartialBookingModal}>Cancel</Button>
                    <Button onClick={onConfirmPartialBooking} autoFocus disabled={!selectedSlots?.length}>
                        Book Now
                    </Button>
                </DialogActions>
            </Dialog>
        </>


    )
}

export default MultiBookingPartial;